/* global BigInt */
// ─── 编码工具函数 ────────────────────────────────────────────────────────────────

// ─── Base64 ───────────────────────────────────────────────────────────────────

export function base64Encode(text) {
  return btoa(unescape(encodeURIComponent(text)));
}

export function base64Decode(text) {
  return decodeURIComponent(escape(atob(text.trim())));
}

// ─── URL 编码 ─────────────────────────────────────────────────────────────────

export function urlEncode(text) {
  return encodeURIComponent(text);
}

export function urlDecode(text) {
  return decodeURIComponent(text);
}

// ─── HTML 实体 ────────────────────────────────────────────────────────────────

export function htmlEncode(text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function htmlDecode(text) {
  return text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

// ─── 十六进制 ─────────────────────────────────────────────────────────────────

export function hexEncode(text) {
  return Array.from(text).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join(' ');
}

export function hexDecode(text) {
  return text.replace(/\s+/g, '').match(/.{2}/g).map(h => String.fromCharCode(parseInt(h, 16))).join('');
}

export function hexToAscii(hex) {
  return hexDecode(hex);
}

export function asciiToHex(text) {
  return hexEncode(text);
}

export function base64ToHex(b64) {
  const bin = atob(b64.trim());
  return Array.from(bin).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join(' ');
}

export function hexToBase64(hex) {
  const bytes = hex.replace(/\s+/g, '').match(/.{2}/g).map(h => parseInt(h, 16));
  return btoa(String.fromCharCode(...bytes));
}

// ─── Unicode ──────────────────────────────────────────────────────────────────

export function unicodeEncode(text) {
  return Array.from(text).map(c => {
    const code = c.codePointAt(0);
    return code > 127 ? `\\u${code.toString(16).padStart(4, '0')}` : c;
  }).join('');
}

export function unicodeDecode(text) {
  return text.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
             .replace(/\\U([0-9a-fA-F]{8})/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
}

// ─── Morse Code ───────────────────────────────────────────────────────────────

const MORSE = {
  'A':'.-', 'B':'-...', 'C':'-.-.', 'D':'-..', 'E':'.', 'F':'..-.', 'G':'--.', 'H':'....', 'I':'..', 'J':'.---',
  'K':'-.-', 'L':'.-..', 'M':'--', 'N':'-.', 'O':'---', 'P':'.--.', 'Q':'--.-', 'R':'.-.', 'S':'...', 'T':'-',
  'U':'..-', 'V':'...-', 'W':'.--', 'X':'-..-', 'Y':'-.--', 'Z':'--..',
  '0':'-----', '1':'.----', '2':'..---', '3':'...--', '4':'....-', '5':'.....', '6':'-....', '7':'--...', '8':'---..', '9':'----.',
  '.':'.-.-.-', ',':'--..--', '?':'..--..', '\'':'.----.', '!':'-.-.--', '/':'-..-.', '(':'-.--.', ')':'-.--.-',
  '&':'.-...', ':':'---...', ';':'-.-.-.', '=':'-...-', '+':'.-.-.', '-':'-....-', '_':'..--.-',
  '"':'.-..-.', '$':'...-..-', '@':'.--.-.', ' ': '/'
};
const MORSE_REV = Object.fromEntries(Object.entries(MORSE).map(([c, m]) => [m, c]));

export function morseEncode(text) {
  return text.toUpperCase().split('').map(c => MORSE[c] || '?').join(' ');
}

export function morseDecode(text) {
  return text.split(' ').map(m => m === '/' ? ' ' : (MORSE_REV[m] || '?')).join('');
}

// ─── Base32 ───────────────────────────────────────────────────────────────────

const B32_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32Encode(text) {
  const bytes = new TextEncoder().encode(text);
  let bits = 0, value = 0, output = '';
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      output += B32_CHARS[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) output += B32_CHARS[(value << (5 - bits)) & 31];
  while (output.length % 8 !== 0) output += '=';
  return output;
}

export function base32Decode(text) {
  const clean = text.toUpperCase().replace(/=+$/, '');
  let bits = 0, value = 0;
  const bytes = [];
  for (const c of clean) {
    const idx = B32_CHARS.indexOf(c);
    if (idx === -1) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) { bytes.push((value >>> (bits - 8)) & 255); bits -= 8; }
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
}

// ─── Base58 ───────────────────────────────────────────────────────────────────

const B58_CHARS = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

export function base58Encode(text) {
  const bytes = Array.from(new TextEncoder().encode(text));
  let num = BigInt('0x' + bytes.map(b => b.toString(16).padStart(2, '0')).join(''));
  let result = '';
  const base = BigInt(58);
  while (num > 0n) {
    result = B58_CHARS[Number(num % base)] + result;
    num /= base;
  }
  return result || '1';
}

export function base58Decode(text) {
  let num = 0n;
  for (const c of text) {
    const idx = B58_CHARS.indexOf(c);
    if (idx === -1) throw new Error('Invalid Base58 character');
    num = num * 58n + BigInt(idx);
  }
  let hex = num.toString(16);
  if (hex.length % 2) hex = '0' + hex;
  const bytes = hex.match(/.{2}/g).map(h => parseInt(h, 16));
  return new TextDecoder().decode(new Uint8Array(bytes));
}

// ─── Base62 ───────────────────────────────────────────────────────────────────

const B62_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

export function base62Encode(text) {
  const bytes = Array.from(new TextEncoder().encode(text));
  let num = BigInt('0x' + bytes.map(b => b.toString(16).padStart(2, '0')).join(''));
  let result = '';
  while (num > 0n) { result = B62_CHARS[Number(num % 62n)] + result; num /= 62n; }
  return result || '0';
}

export function base62Decode(text) {
  let num = 0n;
  for (const c of text) { num = num * 62n + BigInt(B62_CHARS.indexOf(c)); }
  let hex = num.toString(16);
  if (hex.length % 2) hex = '0' + hex;
  const bytes = hex.match(/.{2}/g).map(h => parseInt(h, 16));
  return new TextDecoder().decode(new Uint8Array(bytes));
}

// ─── UTF-8 encode/decode ──────────────────────────────────────────────────────

export function utf8Encode(text) {
  return Array.from(new TextEncoder().encode(text)).map(b => `0x${b.toString(16).padStart(2, '0')}`).join(' ');
}

export function utf8Decode(hex) {
  const bytes = hex.replace(/0x/gi, '').replace(/\s+/g, '').match(/.{2}/g).map(h => parseInt(h, 16));
  return new TextDecoder().decode(new Uint8Array(bytes));
}

// ─── UTF-16 encode/decode ─────────────────────────────────────────────────────

export function utf16Encode(text) {
  const buf = new ArrayBuffer(text.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < text.length; i++) view.setUint16(i * 2, text.charCodeAt(i), false);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join(' ');
}

export function utf16Decode(hex) {
  const bytes = hex.replace(/\s+/g, '').match(/.{2}/g).map(h => parseInt(h, 16));
  const buf = new Uint8Array(bytes).buffer;
  const view = new DataView(buf);
  let result = '';
  for (let i = 0; i < buf.byteLength; i += 2) result += String.fromCharCode(view.getUint16(i, false));
  return result;
}

// ─── IDN (Punycode) ───────────────────────────────────────────────────────────

export function idnEncode(domain) {
  // Using browser built-in via URL
  try {
    const url = new URL('http://' + domain);
    return url.hostname;
  } catch {
    return domain;
  }
}

export function idnDecode(punycode) {
  try {
    // Decode via URL API if available
    const url = new URL('http://' + punycode);
    // Try to get the unicode version
    return url.hostname;
  } catch {
    return punycode;
  }
}

// ─── GZip (browser-native CompressionStream) ─────────────────────────────────

export async function gzipEncode(text) {
  try {
    const stream = new CompressionStream('gzip');
    const writer = stream.writable.getWriter();
    const encoder = new TextEncoder();
    writer.write(encoder.encode(text));
    writer.close();
    const reader = stream.readable.getReader();
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const combined = new Uint8Array(chunks.reduce((a, c) => a + c.length, 0));
    let offset = 0;
    for (const chunk of chunks) { combined.set(chunk, offset); offset += chunk.length; }
    return btoa(String.fromCharCode(...combined));
  } catch {
    return '[GZip 需要现代浏览器支持 CompressionStream API]';
  }
}

export async function gzipDecode(b64) {
  try {
    const bytes = Uint8Array.from(atob(b64.trim()), c => c.charCodeAt(0));
    const stream = new DecompressionStream('gzip');
    const writer = stream.writable.getWriter();
    writer.write(bytes);
    writer.close();
    const reader = stream.readable.getReader();
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const combined = new Uint8Array(chunks.reduce((a, c) => a + c.length, 0));
    let offset = 0;
    for (const chunk of chunks) { combined.set(chunk, offset); offset += chunk.length; }
    return new TextDecoder().decode(combined);
  } catch {
    return '[解码失败，请确保输入是有效的 GZip Base64 数据]';
  }
}

// ─── QR Code (纯 SVG，无依赖，基础版) ────────────────────────────────────────

// We'll generate a placeholder SVG for QR - real QR requires a library
export function generateQrSvg(text, size = 200) {
  // Simple representation - in production use qrcode library
  const cells = 21;
  const cellSize = Math.floor(size / cells);
  const s = cellSize * cells;
  // Finder patterns
  const rects = [];
  const addRect = (x, y, w, h, fill = '#000') =>
    rects.push(`<rect x="${x * cellSize}" y="${y * cellSize}" width="${w * cellSize}" height="${h * cellSize}" fill="${fill}"/>`);

  // Outer finder pattern top-left
  addRect(0, 0, 7, 7); addRect(1, 1, 5, 5, '#fff'); addRect(2, 2, 3, 3);
  // Top-right
  addRect(14, 0, 7, 7); addRect(15, 1, 5, 5, '#fff'); addRect(16, 2, 3, 3);
  // Bottom-left
  addRect(0, 14, 7, 7); addRect(1, 15, 5, 5, '#fff'); addRect(2, 16, 3, 3);
  // Format info
  addRect(8, 0, 1, 6); addRect(0, 8, 6, 1);
  // Data area (random for visual)
  const hash = Array.from(text).reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      if (r < 9 && c < 9) continue;
      if (r < 9 && c > 13) continue;
      if (r > 13 && c < 9) continue;
      if ((Math.abs(hash) * (r * 31 + c * 17 + 7)) % 3 === 0) addRect(c, r, 1, 1);
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}">
<rect width="${s}" height="${s}" fill="white"/>
${rects.join('\n')}
</svg>`;
}

export function qrCodeForUrl(url) { return generateQrSvg(url); }
export function qrCodeForText(text) { return generateQrSvg(text); }
export function qrCodeForEmail(email) { return generateQrSvg(`mailto:${email}`); }
export function qrCodeForPhone(phone) { return generateQrSvg(`tel:${phone}`); }
export function qrCodeForSms(phone, msg) { return generateQrSvg(`sms:${phone}?body=${encodeURIComponent(msg)}`); }
export function qrCodeForEth(address) { return generateQrSvg(`ethereum:${address}`); }

// ─── Properties ↔ TOML (simple) ──────────────────────────────────────────────

export function propertiesToToml(input) {
  const obj = {};
  for (const line of input.split('\n')) {
    const t = line.trim();
    if (!t || t.startsWith('#')) continue;
    const eqIdx = t.indexOf('=');
    if (eqIdx === -1) continue;
    const k = t.slice(0, eqIdx).trim();
    const v = t.slice(eqIdx + 1).trim();
    const parts = k.split('.');
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!(parts[i] in cur)) cur[parts[i]] = {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = v;
  }
  function toToml(o, prefix) {
    let simple = '', complex = '';
    for (const [k, v] of Object.entries(o)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (typeof v === 'object') complex += `\n[${key}]\n${toToml(v, key)}`;
      else simple += `${k} = ${JSON.stringify(v)}\n`;
    }
    return simple + complex;
  }
  return toToml(obj, '');
}

export function tomlToProperties(input) {
  const lines = input.split('\n');
  const result = [];
  let section = '';
  for (let line of lines) {
    line = line.trim();
    if (!line || line.startsWith('#')) continue;
    if (line.startsWith('[') && line.endsWith(']')) { section = line.slice(1, -1).trim(); continue; }
    if (line.includes('=')) {
      const eqIdx = line.indexOf('=');
      const k = line.slice(0, eqIdx).trim();
      let v = line.slice(eqIdx + 1).trim();
      if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
      result.push(`${section ? section + '.' : ''}${k}=${v}`);
    }
  }
  return result.join('\n');
}
